import React, {Component} from 'react'
import {v4 as uuidv4} from 'uuid'
import {Responsive, WidthProvider, ResponsiveProps} from 'react-grid-layout'
import {ExternalViewer} from './external-viewer'
import ViewerLoadingPlaceholder from './viewer-loading-placeholder'
import {
  createCloseViewerMessage,
  createOpenViewerMessage,
  ExternalViewerConfig,
  ExternalViewerKey,
  HandlerAction,
  HandlerActionTable,
  HandlerResponse,
  MessageHandlerContext,
  ViewerMode,
  ViewerDataSource,
  resolveControllerApiUrl,
} from './handler-structures'
import {getObserverToken} from '../../services/observerApi'
import {answerDocumentResources} from './document-resources'
import {answerPrintTitle} from './print-title'
import {resolveStorageSource} from './storage-viewer-source'
import {loadViewerManifest} from '../../services/viewerManifest'
import {viewerFeaturesField} from '../../services/viewerFeatures'
import {WebSocketRequest} from './websocket-request.mjs'
import {ViewerExportActions} from './ViewerExportActions'
import {
  canExport,
  createExportMessage,
  resolveExportFormats,
  ViewerExportFormat,
  ViewerExportFormats,
} from './viewerExport'
import {refreshStaleMetadata} from './viewerMetadataRefresh'
import {Id, toast} from 'react-toastify'
import {toastInfiniteConfig} from '@/shared/config/toastConfig'
import styles from './grid-viewer-manager.module.css'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

/** Shown when a viewer iframe mounts but never handshakes; see {@code failUnstartedViewer}. */
const VIEWER_DID_NOT_START =
  'This viewer could not be started. It may not be available for this version of the app yet — try reopening the file, or contact support if this persists.'

const ResponsiveGridLayout = WidthProvider(Responsive) as React.ComponentType<ResponsiveProps>

interface GridViewerItem {
  id: string
  config: ExternalViewerConfig
  layout: {
    x: number
    y: number
    w: number
    h: number
    i: string
  }
  minimized: boolean
}

interface GridViewerManagerProps {
  title?: string
  viewerType?: 'echart' | 'pathway' | 'workflow'
}

interface GridViewerManagerState {
  viewers: GridViewerItem[]
  layout: any
  breakpoints: {lg: number; md: number; sm: number; xs: number; xxs: number}
  cols: {lg: number; md: number; sm: number; xs: number; xxs: number}
  /** Export formats each iframe reported it implements, keyed by objectInfo.id. Not persisted. */
  exportCaps: Record<string, Partial<ViewerExportFormats>>
}

export const OMICSBOX_BROADCAST_CHANNEL_ID = 'omicsbox_viewer_manager'
export const OMICSBOX_BACKCHANNEL_WS_SERVER_URL = process.env.REACT_APP_BACKCHANNEL_WS_SERVER_URL

const PING_TIMEOUT = 10000
const WAKEUP_TIMEOUT = 5000
const ENABLE_BACKCHANNEL = process.env.REACT_APP_ENABLE_BACKCHANNEL === 'true'

class GridViewerManager
  extends Component<GridViewerManagerProps, GridViewerManagerState>
  implements HandlerActionTable
{
  initializedViewers: {[key: ExternalViewerKey]: React.RefObject<HTMLIFrameElement>}
  backchannelWS: WebSocketRequest
  id: string

  // Keyed by objectInfo.id (the export message's reqId) — see TabViewerManager.exportComplete.
  private exportToasts: Record<string, Id> = {}

  // objectInfo.ids a metadata refresh has been started for, so a restored viewer is re-requested at
  // most once per session — see refreshRestoredMetadata.
  private metadataRefreshAttempted = new Set<string>()

  _pingInterval?: ReturnType<typeof setInterval>
  _lastWakeupCheck: number
  _wakeupInterval?: ReturnType<typeof setInterval>

  constructor(props: GridViewerManagerProps) {
    super(props)

    // Load persisted state from localStorage
    const persistedState = this.loadViewerState()

    this.state = {
      viewers: persistedState.viewers || [],
      layout: persistedState.layout || {},
      breakpoints: {lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0},
      cols: {lg: 12, md: 10, sm: 6, xs: 4, xxs: 2},
      exportCaps: {},
    }

    this.initializedViewers = {}
    this.id = uuidv4()

    if (ENABLE_BACKCHANNEL) {
      this.backchannelWS = new WebSocketRequest({
        url: OMICSBOX_BACKCHANNEL_WS_SERVER_URL,
        serverCallback: this.wsMessageHandler.bind(this),
      })
    } else {
      this.backchannelWS = new WebSocketRequest({websocket: {addEventListener: () => {}}})
    }

    this._lastWakeupCheck = Date.now()
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessageEvent)
    // Warm the viewer manifest before any iframe mounts, so a tab restored from localStorage resolves
    // its version from the manifest on the first paint rather than re-pointing its iframe afterwards.
    void loadViewerManifest()
    this.refreshRestoredMetadata()

    if (ENABLE_BACKCHANNEL) {
      this._wakeupInterval = setInterval(this.wakeupCheck.bind(this), WAKEUP_TIMEOUT)
      this._pingInterval = setInterval(this.pingBackchannel.bind(this), PING_TIMEOUT)

      window.addEventListener('online', this.registerBackchannel.bind(this))

      this.registerBackchannel()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessageEvent)

    if (ENABLE_BACKCHANNEL) {
      window.removeEventListener('online', this.registerBackchannel)

      if (this._pingInterval) {
        clearInterval(this._pingInterval)
      }
      if (this._wakeupInterval) {
        clearInterval(this._wakeupInterval)
      }

      this.unregisterBackchannel()
    }
  }

  // Save viewer state to localStorage
  private saveViewerState = () => {
    // Never persist optimistic loading placeholders (B1) — they are transient. Also clear the
    // transient "initializing" overlay flag so a restored viewer shows its iframe, not a spinner.
    const stateToSave = {
      viewers: this.state.viewers
        .filter((v) => !v.config.loading && !v.config.error)
        .map((v) => (v.config.initializing ? {...v, config: {...v.config, initializing: false}} : v)),
      layout: this.state.layout,
    }
    const storageKey = this.props.viewerType
      ? `grid-viewer-state-${this.props.viewerType}`
      : 'grid-viewer-state'
    localStorage.setItem(storageKey, JSON.stringify(stateToSave))
  }

  // Load viewer state from localStorage
  private loadViewerState = (): Partial<GridViewerManagerState> => {
    try {
      // Try to load from new grid-based storage first
      const storageKey = this.props.viewerType
        ? `grid-viewer-state-${this.props.viewerType}`
        : 'grid-viewer-state'
      let savedState = localStorage.getItem(storageKey)

      if (savedState) {
        const persistedState = JSON.parse(savedState)

        // Migration: TabViewerManager stores { viewersConfigs, selectedTab }
        if (!persistedState.viewers && persistedState.viewersConfigs) {
          const migratedViewers: GridViewerItem[] = (persistedState.viewersConfigs as ExternalViewerConfig[]).map(
            (config: ExternalViewerConfig, index: number) => ({
              id: config.objectInfo.id,
              config,
              layout: {x: 0, y: index * 8, w: 12, h: 8, i: config.objectInfo.id},
              minimized: false,
            })
          )
          const migratedLayout = {
            lg: migratedViewers.map((v) => v.layout),
            md: migratedViewers.map((v) => v.layout),
            sm: migratedViewers.map((v) => v.layout),
            xs: migratedViewers.map((v) => v.layout),
            xxs: migratedViewers.map((v) => v.layout),
          }
          return {viewers: migratedViewers, layout: migratedLayout}
        }

        return {
          viewers: persistedState.viewers || [],
          layout: persistedState.layout || {},
        }
      }

      // If no grid state found, try to migrate from old viewer-manager state
      const oldStorageKey = this.props.viewerType
        ? `viewer-state-${this.props.viewerType}`
        : 'viewer-state'
      savedState = localStorage.getItem(oldStorageKey)

      if (savedState) {
        const oldPersistedState = JSON.parse(savedState)
        // Convert old format to new format
        const migratedViewers: GridViewerItem[] = (oldPersistedState.viewersConfigs || []).map(
          (config: ExternalViewerConfig, index: number) => {
            // Generate layout based on index since we don't have state yet
            const x = 0 // Start at left edge for full width
            const y = index * 8 // Stack vertically with full height spacing

            const defaultLayout = {
              x,
              y,
              w: 12, // Full width
              h: 8,
              i: config.objectInfo.id,
            }

            return {
              id: config.objectInfo.id,
              config: config,
              layout: defaultLayout,
              minimized: false,
            }
          }
        )

        // Generate layout for migrated viewers
        const migratedLayout = {
          lg: migratedViewers.map((viewer) => viewer.layout),
          md: migratedViewers.map((viewer) => viewer.layout),
          sm: migratedViewers.map((viewer) => viewer.layout),
          xs: migratedViewers.map((viewer) => viewer.layout),
          xxs: migratedViewers.map((viewer) => viewer.layout),
        }

        // Save migrated state to new format
        const migratedState = {
          viewers: migratedViewers,
          layout: migratedLayout,
        }
        localStorage.setItem(storageKey, JSON.stringify(migratedState))

        // Clean up old storage
        localStorage.removeItem(oldStorageKey)
        return migratedState
      }
    } catch {
      // Failed to load grid viewer state from localStorage
    }
    return {
      viewers: [],
      layout: {},
    }
  }

  /**
   * Re-request `/metadata` for restored viewers whose persisted metadata predates what the frontend
   * now reads (see {@code VIEWER_METADATA_SCHEMA} in viewerMetadataRefresh).
   *
   * Nothing else refreshes it: `getFileMetadata` is called by the open flow only, and the config
   * persisted here — `controllerInfo` included — is restored verbatim. So a viewer kept open across
   * reloads answers with the capabilities of the day it was opened forever, which is how a table
   * opened before the OBserver advertised `SAVE_AS_CSV_ALLOWED` ends up with no export icons while
   * the same file opened today has them.
   *
   * Unlike the tabbed manager, every restored viewer here is mounted at once, so they are all
   * refreshed on mount — each at most once per session.
   */
  private refreshRestoredMetadata = () => {
    refreshStaleMetadata({
      configs: this.state.viewers.map((viewer) => viewer.config),
      attempted: this.metadataRefreshAttempted,
      onRefreshed: this.applyRefreshedMetadata,
    })
  }

  /**
   * Swap a refreshed config in place, keeping the panel's position and layout, and persist the fresh
   * answer so the next reload starts from it. Viewers closed while the request was in flight are not
   * resurrected — only an entry that is still there is replaced.
   */
  private applyRefreshedMetadata = (refreshed: ExternalViewerConfig) => {
    this.setState(
      (prev) => ({
        viewers: prev.viewers.map((viewer) =>
          String(viewer.config.objectInfo.id) === String(refreshed.objectInfo.id)
            ? {...viewer, config: refreshed}
            : viewer
        ),
      }),
      this.saveViewerState
    )
  }

  // Clear viewer state from localStorage
  private clearViewerState = () => {
    const storageKey = this.props.viewerType
      ? `grid-viewer-state-${this.props.viewerType}`
      : 'grid-viewer-state'
    localStorage.removeItem(storageKey)
  }

  // Clear all viewers
  private clearAllViewers = () => {
    this.setState(
      {
        viewers: [],
        layout: {},
      },
      () => {
        this.clearViewerState()
      }
    )
  }

  // Toggle minimize state for a viewer
  private toggleMinimize = (viewerId: string) => {
    // Temporarily disable transitions for smooth minimize/maximize
    const gridLayoutElement = document.querySelector('.react-grid-layout')
    if (gridLayoutElement) {
      gridLayoutElement.classList.add('no-transition')
    }

    const updatedViewers = this.state.viewers.map((viewer) => {
      if (viewer.id === viewerId) {
        return {
          ...viewer,
          minimized: !viewer.minimized,
        }
      }
      return viewer
    })

    // Update layout to reflect minimized state
    const updatedLayout = {...this.state.layout}
    Object.keys(updatedLayout).forEach((breakpoint) => {
      updatedLayout[breakpoint] = updatedLayout[breakpoint].map((item: any) => {
        if (item.i === viewerId) {
          const viewer = updatedViewers.find((v) => v.id === viewerId)
          if (viewer?.minimized) {
            // Minimize: reduce height to just show header
            return {...item, h: 1}
          } else {
            // Restore: return to original height
            return {...item, h: 8}
          }
        }
        return item
      })
    })

    this.setState(
      {
        viewers: updatedViewers,
        layout: updatedLayout,
      },
      () => {
        this.saveViewerState()
        
        // Re-enable transitions after a short delay
        setTimeout(() => {
          if (gridLayoutElement) {
            gridLayoutElement.classList.remove('no-transition')
          }
        }, 50)
      }
    )
  }

  // Handle layout change
  private onLayoutChange = (layout: any, layouts: any) => {
    this.setState({layout: layouts}, this.saveViewerState)
  }

  // Generate default layout for a new viewer
  private generateDefaultLayout = (viewerId: string): any => {
    // Always place new viewers at the top
    const x = 0
    const y = 0

    return {
      x,
      y,
      w: 10, // ~1200px on typical screens, resizable wider
      h: 8,
      i: viewerId,
    }
  }

  wsMessageHandler = (msg: string | JSON) => {
    const _messageHandlers: Record<string, any> = {
      changeViewerStatus: (msgData: any) => {
        const viewerStatus = msgData.data
        this.changeViewerStatus(viewerStatus.data.viewerId, viewerStatus.data.mode)
        this.forceUpdate()

        this.backchannelWS.trySend({
          reqid: msgData.reqid,
          data: {
            action: 'changeViewerStatus',
            changed: true,
            viewerId: viewerStatus.viewerId,
          },
        })
      },
    }

    const msgData = typeof msg === 'string' ? JSON.parse(msg) : msg
    const msgAction = msgData.data.action as string

    if (msgAction in _messageHandlers) {
      _messageHandlers[msgAction](msgData)
    }
  }

  private changeViewerStatus(viewerId: string, status: ViewerMode) {
    const updatedViewers = this.state.viewers.map((viewer) => {
      if (viewer.config.objectInfo.id === viewerId) {
        return {
          ...viewer,
          config: {
            ...viewer.config,
            mode: status,
          },
        }
      }
      return viewer
    })

    this.setState({viewers: updatedViewers})
  }

  private registerBackchannel = () => {
    this.backchannelWS.sendJson(
      {
        action: 'register',
        viewer: {
          id: this.id,
          viewers: this.state.viewers.map((v) => v.config),
        },
      },
      (data: any) => {
        const requestedChanges = data.modeChanges ?? {}

        Object.keys(requestedChanges).forEach((viewerId) => {
          this.changeViewerStatus(viewerId, requestedChanges[viewerId])
        })
      }
    )
  }

  private unregisterBackchannel = () => {
    this.backchannelWS.sendJson({
      action: 'unregister',
      viewer: {
        id: this.id,
      },
    })
  }

  private wakeupCheck = () => {
    let currentTime = Date.now()

    if (currentTime > this._lastWakeupCheck + WAKEUP_TIMEOUT + 5000) {
      this.registerBackchannel()
    }

    this._lastWakeupCheck = currentTime
  }

  private pingBackchannel = () => {
    this.backchannelWS.sendJson(
      {
        action: 'ping',
        viewer: {
          id: this.id,
        },
      },
      (data: any) => {
        if (data.success === false) {
          this.registerBackchannel()
        }
      }
    )
  }

  private updateViewers = () => {
    this.backchannelWS.sendJson({
      action: 'updateViewers',
      viewer: {
        id: this.id,
        viewers: this.state.viewers.map((v) => v.config),
      },
    })
  }

  private getPropsById = (id: ExternalViewerKey): ExternalViewerConfig | undefined => {
    const viewer = this.state.viewers.find((v) => String(v.config.objectInfo.id) === String(id))
    return viewer?.config
  }

  private getPropsByRef = (ref: MessageEventSource): ExternalViewerConfig | undefined => {
    // Try same-origin frameElement access first
    try {
      if ('frameElement' in ref) {
        const sourceIframe = (ref as Window).frameElement as HTMLIFrameElement
        if (sourceIframe) {
          const iframeId = Object.keys(this.initializedViewers).find(
            (key) => this.initializedViewers[key].current === sourceIframe
          )
          if (iframeId) {
            return this.getPropsById(iframeId)
          }
        }
      }
    } catch {
      // Cross-origin: frameElement access blocked by browser security
    }

    // Fallback for cross-origin iframes: match by contentWindow reference
    const iframeId = Object.keys(this.initializedViewers).find(
      (key) => this.initializedViewers[key].current?.contentWindow === ref
    )
    if (iframeId) {
      return this.getPropsById(iframeId)
    }

    return undefined
  }

  // Clear the "initializing" overlay for a viewer, revealing its iframe (B1/#3). Idempotent.
  private clearInitializing = (id: string) => {
    if (!this.state.viewers.some((v) => v.config.objectInfo.id === id && v.config.initializing)) {
      return
    }
    this.setState(
      {
        viewers: this.state.viewers.map((v) =>
          v.config.objectInfo.id === id ? {...v, config: {...v.config, initializing: false}} : v
        ),
      },
      this.saveViewerState
    )
  }

  /**
   * A viewer that never asked for its API URL never started; show that as an error rather than
   * revealing the iframe. Idempotent, and a no-op once the viewer has handshaken.
   *
   * The error message matters because the usual cause is invisible: an iframe pointed at a viewer
   * version that is not published is answered by the CDN with the OmicsBox app's own index.html
   * (HTTP 200), so the frame loads successfully and would otherwise show the entire app nested
   * inside the tab. Because error configs are excluded from persistence, this also keeps a broken
   * tab from surviving a reload.
   */
  private failUnstartedViewer = (id: string) => {
    if (!this.state.viewers.some((v) => v.config.objectInfo.id === id && v.config.initializing)) {
      return
    }
    this.setState(
      {
        viewers: this.state.viewers.map((v) =>
          v.config.objectInfo.id === id
            ? {...v, config: {...v.config, initializing: false, error: VIEWER_DID_NOT_START}}
            : v
        ),
      },
      this.saveViewerState
    )
  }

  getApiUrl = async (e: MessageEvent): Promise<void> => {
    if (e.source) {
      const iframeEl = e.source
      const viewerProps = this.getPropsByRef(iframeEl)

      if (viewerProps) {
        // Ready to load data — hide the loading overlay so the placeholder→viewer swap has no flash.
        this.clearInitializing(viewerProps.objectInfo.id)
        // Storage-backed viewer: reads the file directly from S3, so hand it a presigned URL + the
        // OBserver-resolved format instead of a controller endpoint. Generic — driven by dataSource,
        // not by the viewer's name.
        if (viewerProps.controllerInfo.dataSource === ViewerDataSource.STORAGE && viewerProps.objectInfo.objectUri) {
          const source = await resolveStorageSource(viewerProps.objectInfo.objectUri, viewerProps.objectInfo.type, viewerProps.share)
          // @ts-ignore
          iframeEl.postMessage({reqId: viewerProps.objectInfo.id, action: 'getApiUrl', ...source, ...viewerFeaturesField()}, '*')
          return
        }
        // Hand the iframe the ID token so its /controller requests to the OBserver authenticate.
        const token = await getObserverToken()
        const actionResponse = {
          reqId: viewerProps.objectInfo.id,
          value: resolveControllerApiUrl(viewerProps.controllerInfo),
          action: 'getApiUrl',
          token,
          // What this deployment wants the viewer to offer; omitted entirely when it declares
          // nothing. See services/viewerFeatures.ts — this is why develop and release differ
          // while running the identical viewer bundle.
          ...viewerFeaturesField(),
        }

        // @ts-ignore
        iframeEl.postMessage(actionResponse, '*')

        // The viewer is listening now, so ask what it can export. Until it answers, no export icons
        // are shown — better than offering a format the viewer has no handler for.
        // @ts-ignore
        iframeEl.postMessage({reqId: viewerProps.objectInfo.id, action: 'getExportCapabilities'}, '*')
      }
    }
  }

  // A storage-backed document asks for the images it references to be signed (see document-resources.ts).
  resolveDocumentResources = (e: MessageEvent): Promise<void> =>
    answerDocumentResources(e, e.source ? this.getPropsByRef(e.source) : undefined)

  // A viewer about to print asks for the page title that names the saved PDF (see print-title.ts).
  setPrintTitle = (e: MessageEvent): void =>
    answerPrintTitle(e, e.source ? this.getPropsByRef(e.source) : undefined)

  // An iframe's OBserver call came back 401 (baked-in token expired). Hand it a fresh token
  // (Auth.currentSession() auto-refreshes) so it can retry without being reopened.
  refreshToken = async (e: MessageEvent): Promise<void> => {
    if (!e.source) return
    const token = await getObserverToken()
    // @ts-ignore
    e.source.postMessage({action: 'refreshToken', token}, '*')
  }

  openViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    /**
     * FILE TYPE MAPPING SYSTEM:
     *
     * This function normalizes API type strings to our internal viewer types.
     * The metadata API can return various type strings that need to be mapped
     * to our three viewer types: 'echart', 'pathway', 'workflow'.
     *
     * API Types → Normalized Types:
     * - "Chart", "chart", "echart", "Echart" → "echart" (Charts viewer)
     * - "Pathway", "pathway", "PathwaysAnalysisObject", "pathwaysanalysisobject" → "pathway" (Pathway Analysis viewer)
     * - "Workflow", "workflow" → "workflow" (Workflows page)
     *
     * IMPORTANT: This mapping must be kept in sync with:
     * 1. ResultsPage.tsx and WorkflowsPage.tsx type handling
     * 2. FileTable.tsx routing logic
     * 3. Any new file types added to the system
     */
    const mapViewerType = (type: string): string => {
      const typeMap: Record<string, string> = {
        Chart: 'echart',
        chart: 'echart',
        echart: 'echart',
        Echart: 'echart',
        Pathway: 'pathway',
        pathway: 'pathway',
        PathwaysAnalysisObject: 'pathway',
        pathwaysanalysisobject: 'pathway',
        Workflow: 'workflow',
        workflow: 'workflow',
      }
      return typeMap[type] || type.toLowerCase()
    }

    const normalizedViewerType = mapViewerType(viewerConfig.objectInfo.type)

    // Check if this viewer manager should accept this viewer type. Loading placeholders (B1) carry a
    // synthetic 'loading' type and must bypass this filter; the real config is filtered normally.
    if (!viewerConfig.loading && this.props.viewerType && normalizedViewerType !== this.props.viewerType) {
      return // This viewer manager doesn't handle this type
    }

    // Real config resolving an optimistic placeholder: swap the placeholder's config in place,
    // keeping its grid slot/layout, so the spinner becomes the viewer without a re-layout.
    if (!viewerConfig.loading && viewerConfig.correlationId) {
      const placeholderIndex = this.state.viewers.findIndex(
        (v) => v.config.loading && v.config.correlationId === viewerConfig.correlationId
      )
      if (placeholderIndex >= 0) {
        const alreadyOpen = this.state.viewers.some(
          (v, i) => i !== placeholderIndex && !v.config.loading && v.config.objectInfo.id === viewerConfig.objectInfo.id
        )
        if (alreadyOpen) {
          // Duplicate already open elsewhere: just drop the placeholder.
          window.postMessage(createCloseViewerMessage(this.state.viewers[placeholderIndex].config))
          return
        }
        const newViewers = [...this.state.viewers]
        newViewers[placeholderIndex] = {...newViewers[placeholderIndex], config: viewerConfig}
        this.setState({viewers: newViewers}, () => {
          this.saveViewerState()
          this.updateViewers()
        })
        // Safety net: drop the overlay even if the viewer never requests its API URL.
        if (viewerConfig.initializing) {
          setTimeout(() => this.failUnstartedViewer(viewerConfig.objectInfo.id), 15000)
        }
        return
      }
    }

    // Check if viewer is already open
    const existingViewer = this.state.viewers.find(
      (viewer) => !viewer.config.loading && viewer.config.objectInfo.id === viewerConfig.objectInfo.id
    )

    if (existingViewer) {
      // Viewer already exists, just bring it to front (could implement focus logic here)
      return
    }

    // Add new viewer with generated layout
    const viewerId = viewerConfig.objectInfo.id
    const defaultLayout = this.generateDefaultLayout(viewerId)

    const newViewer: GridViewerItem = {
      id: viewerId,
      config: viewerConfig,
      layout: defaultLayout,
      minimized: false,
    }

    const newViewers = [...this.state.viewers, newViewer]

    // Shift all existing viewers down by the height of the new viewer (8 units)
    const shiftedLayout = {...this.state.layout}
    Object.keys(shiftedLayout).forEach((breakpoint) => {
      if (shiftedLayout[breakpoint]) {
        // Shift existing items down
        const shiftedItems = shiftedLayout[breakpoint].map((item: any) => ({
          ...item,
          y: item.y + 8, // Shift down by new viewer height
        }))
        // Add new viewer at the top
        shiftedLayout[breakpoint] = [defaultLayout, ...shiftedItems]
      } else {
        // If no existing layout for this breakpoint, just add the new viewer
        shiftedLayout[breakpoint] = [defaultLayout]
      }
    })

    const newLayout = shiftedLayout

    this.setState(
      {
        viewers: newViewers,
        layout: newLayout,
      },
      () => {
        this.saveViewerState()
        this.updateViewers()
      }
    )
  }

  requestViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    // Skip if already open — UNLESS this config resolves an optimistic placeholder (correlationId):
    // then it must still reach openViewer so the leftover placeholder gets reconciled/removed.
    const alreadyOpen = this.state.viewers.some(
      (viewer) =>
        !viewer.config.loading &&
        viewer.config.objectInfo.id.toString() === viewerConfig.objectInfo.id.toString()
    )
    if (!alreadyOpen || viewerConfig.correlationId) {
      if (ENABLE_BACKCHANNEL) {
        this.backchannelWS.sendJson(
          {
            action: 'isViewerLocked',
            viewer: {
              id: this.id,
              config: viewerConfig,
            },
          },
          (response: any) => {
            const isLocked = response.locked
            const viewerConfig = response.config

            viewerConfig.mode = isLocked ? ViewerMode.READ : ViewerMode.WRITE

            window.postMessage(createOpenViewerMessage(viewerConfig), '/')
          }
        )
      } else {
        window.postMessage(createOpenViewerMessage(viewerConfig), '/')
      }
    }
  }

  closeViewer = (e: MessageEvent) => {
    const viewerConfig = e.data.value as ExternalViewerConfig

    if (
      this.state.viewers.some(
        (viewer) => viewer.config.objectInfo.id === viewerConfig.objectInfo.id
      )
    ) {
      const newViewers = this.state.viewers.filter(
        (viewer) => viewer.config.objectInfo.id !== viewerConfig.objectInfo.id
      )

      // Remove from layout as well
      const newLayout = {...this.state.layout}
      Object.keys(newLayout).forEach((breakpoint) => {
        newLayout[breakpoint] = newLayout[breakpoint].filter(
          (item: any) => item.i !== viewerConfig.objectInfo.id
        )
      })

      this.setState(
        {
          viewers: newViewers,
          layout: newLayout,
        },
        () => {
          this.updateViewers()
          this.saveViewerState()
        }
      )
    }
  }

  handleMessageEvent = (e: MessageEvent) => {
    // Anything can post to window — extensions post strings, dev tooling posts nulls — and reading
    // .action off one of those throws inside a global listener.
    if (typeof e.data !== 'object' || e.data === null) return

    const handlerAction = e.data.action as HandlerAction

    if (handlerAction in this) {
      this[handlerAction](e)
    }
  }

  registerViewer = (key: string, iframe: React.RefObject<HTMLIFrameElement>) => {
    this.initializedViewers[key] = iframe
  }

  unregisterViewer = (key: string) => {
    delete this.initializedViewers[key]
  }

  sendMessageToViewer = (iframe: React.RefObject<HTMLIFrameElement>, response: HandlerResponse) => {
    if (iframe.current?.contentWindow) {
      iframe.current.contentWindow.postMessage(response, '*')
    }
  }

  /**
   * Ask a grid panel's iframe to export (getPNG / getPDF / getCSV). The iframe produces the payload
   * and its ConnectionManager decides where it goes — see TabViewerManager.handleExport.
   */
  handleExport = (viewerId: string, format: ViewerExportFormat) => {
    const viewer = this.state.viewers.find((v) => v.id === viewerId)
    const iframe = viewer && this.initializedViewers[viewer.config.objectInfo.id]
    if (!canExport(viewer?.config, iframe)) {
      return
    }
    this.exportToasts[viewer!.config.objectInfo.id] = toast.loading(
      `Exporting ${format.toUpperCase()}…`,
      toastInfiniteConfig
    )
    this.sendMessageToViewer(
      iframe,
      createExportMessage(format, viewer!.config.objectInfo.id, viewer!.config.objectInfo.name)
    )
  }

  /**
   * The iframe's fetch for a server-rendered export (PDF/TSV) finished — resolve the "loading"
   * toast handleExport started. See TabViewerManager.exportComplete.
   */
  exportComplete = (e: MessageEvent) => {
    const {reqId, kind, success} = e.data as {reqId?: string; kind?: string; success?: boolean}
    if (!reqId) return
    const toastId = this.exportToasts[reqId]
    if (toastId === undefined) return
    delete this.exportToasts[reqId]
    toast.update(toastId, {
      render: success ? `${(kind ?? 'Export').toUpperCase()} export ready` : 'Export failed',
      type: success ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      isLoading: false,
      autoClose: 3000,
    })
  }

  /**
   * An iframe reported which export formats it implements (reply to getExportCapabilities). Only
   * the intersection with the controller's workbenchProperties gets an icon.
   */
  exportCapabilities = (e: MessageEvent) => {
    if (!e.source) return
    const props = this.getPropsByRef(e.source)
    if (!props) return
    const value = e.data.value as Partial<ViewerExportFormats> | undefined
    this.setState((prev) => ({
      exportCaps: {...prev.exportCaps, [props.objectInfo.id]: value ?? {}},
    }))
  }

  handleRequestMode = (key: string, mode: ViewerMode) => {
    this.backchannelWS.sendJson(
      {
        action: 'requestOwnership',
        requestedViewerId: key,
        requestedMode: mode,
        viewer: {
          id: this.id,
        },
      },
      (message: any) => {
        const availableMode = message.mode ?? ViewerMode.READ
        const updatedViewers = this.state.viewers.map((viewer) => {
          if (String(viewer.config.objectInfo.id) === String(key)) {
            return {
              ...viewer,
              config: {
                ...viewer.config,
                mode: availableMode,
              },
            }
          }
          return viewer
        })

        this.setState({viewers: updatedViewers})
        this.updateViewers()
      }
    )
  }

  getMessageHandlerProvider() {
    return {
      registerViewer: this.registerViewer,
      unregisterViewer: this.unregisterViewer,
      sendMessage: this.sendMessageToViewer,
      handleAction: this.handleMessageEvent,
      requestMode: this.handleRequestMode,
    }
  }

  render() {
    // Only render if we have viewers
    if (this.state.viewers.length === 0) {
      return null
    }

    return (
      <MessageHandlerContext.Provider value={this.getMessageHandlerProvider()}>
        <div className={styles.gridViewerManagerContainer}>
          {/* Grid Layout */}
          <div className={styles.gridViewerManagerContent}>
            {React.createElement(
              ResponsiveGridLayout as any,
              {
                className: 'layout',
                layouts: this.state.layout,
                breakpoints: this.state.breakpoints,
                cols: this.state.cols,
                rowHeight: 60,
                onLayoutChange: this.onLayoutChange,
                isDraggable: true,
                isResizable: true,
                draggableHandle: '.dragHandle',
                margin: [16, 16],
                containerPadding: [0, 0],
                useCSSTransforms: true,
                
              },
              this.state.viewers.map((viewer) => (
                <div
                  key={viewer.id}
                  className={`${styles.gridViewerItem} ${viewer.minimized ? styles.minimized : ''}`}
                >
                  {/* Drag Handle and Title */}
                  <div className={`${styles.dragHandle} dragHandle`}>
                    <span className={styles.viewerTitle}>{viewer.config.objectInfo.name}</span>
                    <div className={styles.viewerControls}>
                      {!viewer.config.loading && !viewer.config.error && (
                        <ViewerExportActions
                          formats={resolveExportFormats(
                            viewer.config.controllerInfo?.workbenchProperties,
                            this.state.exportCaps[viewer.config.objectInfo.id]
                          )}
                          onExport={(format) => this.handleExport(viewer.id, format)}
                        />
                      )}
                      <button
                        className={styles.controlButton}
                        onMouseDown={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          this.toggleMinimize(viewer.id)
                        }}
                        title={viewer.minimized ? 'Maximize' : 'Minimize'}
                      >
                        <i className={`fas ${viewer.minimized ? 'fa-expand' : 'fa-compress'}`}></i>
                      </button>
                      <button
                        className={styles.closeButton}
                        onMouseDown={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          window.postMessage(createCloseViewerMessage(viewer.config))
                        }}
                        title='Close viewer'
                      >
                        <i className='fas fa-times'></i>
                      </button>
                    </div>
                  </div>

                  {/* Viewer Content */}
                  <div className={styles.viewerContent}>
                    {viewer.config.error ? (
                      <ViewerLoadingPlaceholder
                        error={viewer.config.error}
                        errorKind={viewer.config.errorKind}
                        name={viewer.config.objectInfo.name}
                      />
                    ) : viewer.config.loading ? (
                      <ViewerLoadingPlaceholder
                        message={viewer.config.loadingMessage}
                        name={viewer.config.objectInfo.name}
                      />
                    ) : (
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          width: '100%',
                          minHeight: 0,
                        }}
                      >
                        <ExternalViewer {...viewer.config} />
                        {viewer.config.initializing && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              zIndex: 2,
                              background: 'var(--ob-body-bg, var(--bs-body-bg, #ffffff))',
                            }}
                          >
                            <ViewerLoadingPlaceholder
                              message={viewer.config.loadingMessage}
                              name={viewer.config.objectInfo.name}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </MessageHandlerContext.Provider>
    )
  }
}

export default GridViewerManager
